/**
 * THE FILTER OBJECT — the entire query, in one place.
 *
 * This object IS three things at once, and that is the design rather than a
 * coincidence:
 *
 *   · the query every request on the screen is derived from,
 *   · the URL, so a view is shareable and survives a refresh,
 *   · a saved view, so saving is naming a string rather than a feature.
 *
 * Getting those three to be one object is what makes Save cheap (§6 of
 * CONSOLIDATION_PLAN.md). The moment any of them keeps state of its own — a
 * component holding "which series are hidden", a table holding its own sort —
 * a saved view stops restoring the screen it was saved from, and the failure is
 * silent.
 *
 * ── EVERY ENUM VALUE IS VALIDATED AGAINST `meta`, NEVER TRUSTED ──
 *
 * `/analytics/meta` is a capability descriptor: the range enum, the measures and
 * each measure's OWN legal dimension list, the entry kinds, the severity levels,
 * and the buildings and portfolios THIS CALLER can get an answer about. A value
 * that is not in it is not a filter, it is a 400 — or worse, a building id the
 * caller cannot see, which RLS narrows to nothing and which the screen would
 * then render as a confident set of zeros for somebody else's building.
 *
 * So `normalise()` in filter-reducer.ts drops what meta does not admit, and the
 * URL codec never writes a value it could not read back. This is the same rule
 * dashboard-analytics.tsx:113-117 already enforced; it is moved here rather than
 * reinvented.
 */

/* ─────────────────────────────  vocabulary  ───────────────────────────── */

export const TABS = ['overview', 'trends', 'activity', 'records', 'reports'] as const;
export type Tab = (typeof TABS)[number];

export const GRANULARITIES = ['day', 'week', 'month'] as const;
export type Granularity = (typeof GRANULARITIES)[number];

export const DENSITIES = ['comfortable', 'compact'] as const;
export type Density = (typeof DENSITIES)[number];

/**
 * THE FOUR BOOKS. The VALUES are the wire — every existing client, saved filter
 * and test sends these exact strings, and CRUD/reports.js validates against
 * them (LOG_TYPES). The LABELS are what a reader sees, and two of them differ
 * from the wire on purpose: the daily log is called Building Activities
 * everywhere a person can read it, and "Visitors" now excludes residents.
 */
export const BOOKS = [
  'Visitor Log',
  'Resident Log',
  'Parcel Log',
  'Daily Log',
] as const;
export type Book = (typeof BOOKS)[number];

export const BOOK_LABELS: Record<Book, string> = {
  'Visitor Log': 'Visitors',
  'Resident Log': 'Residents',
  'Parcel Log': 'Parcels',
  'Daily Log': 'Building Activities',
};

/** The range name that means "the from/to instants below are the window". */
export const CUSTOM_RANGE = 'custom';

export const DIRECTIONS = ['in', 'out'] as const;
export type Direction = (typeof DIRECTIONS)[number];

export const STATUSES = ['open', 'closed'] as const;
export type Status = (typeof STATUSES)[number];

export const PARCEL_STATUSES = ['pending', 'picked_up', 'returned'] as const;

export const ACTIVITY_GROUPS = [
  'residents',
  'visitors',
  'parcels',
  'daily_log',
  'corrections',
] as const;

/* ─────────────────────────────  the object  ───────────────────────────── */

/**
 * What a chart click produced.
 *
 * `label` is stored so the chip reads the way the chart read — but it is only a
 * cache: the chip re-derives the label from live data when it can, so a link
 * pasted a week later cannot print a name that has since changed.
 */
export interface DrillSelection {
  /** The panel id that produced it — for the chip label and for undo. */
  source: string;
  /** The dimension the mark encodes: building | kind | severity | staff | … */
  dimension: string;
  /** The group key, verbatim from the API item. */
  key: string;
  /** The label as it was rendered when the click happened. */
  label: string;
}

export interface SortState {
  column: string;
  dir: 'asc' | 'desc';
}

export interface CompareScope {
  portfolioId: number | null;
  buildingId: number | null;
}

export interface DashboardFilter {
  /* ── SCOPE ──
   * Absence of all three is ORG-WIDE, and RLS decides what that means. A caller
   * cannot widen their own answer by clearing these. */
  portfolioId: number | null;
  buildingId: number | null;
  /** unit_number — the value the log rows actually store in apartmentLocation. */
  unit: string | null;

  /* ── TIME ── */
  /** A meta.ranges member, or CUSTOM_RANGE. */
  range: string;
  /** DAY KEYS ('YYYY-MM-DD'), BOTH INCLUSIVE (R-T5). Meaningful only with
   *  range === CUSTOM_RANGE; both or neither. A picked day travels as the day
   *  it is and the SERVER turns it into instants on the reader's clock — these
   *  were ISO instants at browser-local midnight with an exclusive `to`, and
   *  everything that sliced them back to a date was a day off east of Greenwich. */
  from: string | null;
  to: string | null;
  granularity: Granularity;
  compare: string;
  /** HH:mm, 24-hour. The reports books' time-of-day narrowing. */
  timeFrom: string | null;
  timeTo: string | null;

  /* ── SUBJECT ── */
  /** meta.measures key — what Trends counts. */
  measure: string;
  /** Which of the four books Records and Reports read. */
  book: Book;
  /** meta.latency.subjects key — the dwell panel. */
  subject: string;
  turnoverBucket: Granularity;

  /* ── DIMENSIONS. AND across fields, OR within an array. ── */
  kinds: string[];
  severity: string | null;
  groups: string[];
  /** User ids. Who WROTE IT DOWN — never who was on duty. */
  actors: string[];
  role: string | null;
  direction: Direction | null;
  status: Status | null;
  priority: string | null;
  buildingActivity: string | null;
  visitorType: string | null;
  visitorDetails: string | null;
  preAuthorized: string | null;
  /** 'Yes' / 'No' — did the visit come on a visitor pass (resident-portal
   *  Plan 5). Visitors book only; derived server-side, never the pass id. */
  onPass: string | null;
  approval: string | null;
  parkingValidation: string | null;
  numberOfGuests: number | null;
  carrier: string | null;
  parcelStatus: string | null;
  name: string | null;
  lastName: string | null;

  /* ── FREE TEXT ── */
  q: string;

  /* ── VIEW STATE ──
   * Travels with the view and is restored by a saved view, but consumes no
   * request slice (see filter-slices.ts), so changing it refetches nothing. */
  tab: Tab;
  drill: DrillSelection | null;
  qualityDrill: string | null;
  /** Series toggled OFF in a legend, as `<panelId>:<seriesKey>`. */
  seriesOff: string[];
  density: Density;
  /** Visible Records columns; null means the book's default set. */
  columns: string[] | null;
  sort: SortState | null;
  page: number;
  compareScope: CompareScope | null;
}

/**
 * The starting view.
 *
 * `range`, `compare`, `measure`, `subject` and `turnoverBucket` are re-seeded
 * from `meta` the moment it lands (meta.defaultRange etc.), because the server
 * owns those defaults. The literals here are what the screen uses for the one
 * render before meta arrives, and they are the server's current values so the
 * first paint does not shift.
 */
export const DEFAULT_FILTER: DashboardFilter = Object.freeze({
  portfolioId: null,
  buildingId: null,
  unit: null,

  range: 'last_30_days',
  from: null,
  to: null,
  granularity: 'day',
  compare: 'previous_period',
  timeFrom: null,
  timeTo: null,

  measure: 'entries',
  book: 'Visitor Log',
  subject: 'parcel',
  turnoverBucket: 'day',

  kinds: [],
  severity: null,
  groups: [],
  actors: [],
  role: null,
  direction: null,
  status: null,
  priority: null,
  buildingActivity: null,
  visitorType: null,
  visitorDetails: null,
  preAuthorized: null,
  onPass: null,
  approval: null,
  parkingValidation: null,
  numberOfGuests: null,
  carrier: null,
  parcelStatus: null,
  name: null,
  lastName: null,

  q: '',

  tab: 'overview',
  drill: null,
  qualityDrill: null,
  seriesOff: [],
  density: 'comfortable',
  columns: null,
  sort: null,
  page: 0,
  compareScope: null,
}) as DashboardFilter;

/* ────────────────────  ROLE-TAILORED DEFAULTS (QA-07)  ────────────────────
 *
 * "The default view should foreground the most common decisions and
 * progressively disclose specialist filters." The three staff roles open this
 * screen to answer three different questions, and DEFAULT_FILTER above answers
 * the owner's.
 *
 *   owner             the whole organisation, thirty days, Overview. Unchanged —
 *                     it is the portfolio-level question the screen was built
 *                     for, and changing it would move the ground under every
 *                     existing user.
 *   property_manager  the same breadth, but managers are read from this screen
 *                     for OPERATIONS rather than trends, so they land on
 *                     Overview with a seven-day window: "what happened this
 *                     week on my buildings" is the question, and thirty days
 *                     buries a Tuesday incident in a month of noise.
 *   concierge         a concierge works ONE building on ONE shift (R-3). The
 *                     Records tab over today is the entirety of what this screen
 *                     is to them; a thirty-day organisation-wide trend is not a
 *                     question they can act on.
 *
 * ── WHAT THIS DELIBERATELY DOES NOT DO ──
 *
 * It does not narrow SCOPE. `buildingId` stays null for every role, because the
 * boundary is RLS and the grants behind it — not a default on a control. A
 * default that pinned a concierge to "their" building would be a second,
 * client-side answer to a question the server already answers, and the two would
 * disagree the first time somebody was transferred.
 *
 * It is also only a DEFAULT: it applies when the address bar carries no filter
 * at all. A pasted link, a saved view and the back button all still win, which
 * is what keeps a shared link meaning the same thing for everybody who opens it.
 */
export const roleDefaults = (role?: string | null): DashboardFilter => {
  switch (role) {
    case 'property_manager':
      return { ...DEFAULT_FILTER, range: 'last_7_days', tab: 'overview' };
    case 'concierge':
      return { ...DEFAULT_FILTER, range: 'today', tab: 'records' };
    default:
      return { ...DEFAULT_FILTER };
  }
};

/* ───────────────────────────  the meta contract  ──────────────────────── */

/**
 * WHAT THE STATE LAYER NEEDS FROM /analytics/meta — and nothing more.
 *
 * Narrower than `AnalyticsMeta` on purpose. The reducer and the codec are pure
 * and are tested without the API client, without axios and without a network
 * stub; depending on the full response type would drag all three into a test
 * about string parsing. `filterMetaFrom()` below is the one adapter.
 */
export interface FilterMeta {
  ranges: string[];
  defaultRange: string;
  compare: string[];
  defaultCompare: string;
  measures: { key: string; label: string; dimensions: string[] }[];
  defaultMeasure: string;
  kinds: string[];
  severity: string[];
  /* `portfolioId` is the ONLY place the building -> portfolio link exists on
     the client: /analytics/breakdown returns one dimension per panel, never the
     nesting, so a roll-up that shows both levels has to assemble it from here. */
  buildings: { id: number; name: string; portfolioId: number | null }[];
  portfolios: { id: number; name: string }[];
  latencySubjects: string[];
  defaultSubject: string;
  turnoverBuckets: string[];
  defaultTurnoverBucket: string;
  qualityDrills: string[];
  rhythmMeasures: string[];
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * AnalyticsMeta -> FilterMeta. Defensive at every step: this runs against a
 * response, and a missing branch must degrade to "no constraint" rather than
 * throw on a screen that has not painted yet.
 */
export const filterMetaFrom = (meta: any): FilterMeta => ({
  ranges: meta?.ranges || [],
  defaultRange: meta?.defaultRange || DEFAULT_FILTER.range,
  compare: meta?.compare || [],
  defaultCompare: meta?.defaultCompare || DEFAULT_FILTER.compare,
  measures: (meta?.measures || []).map((m: any) => ({
    key: m.key,
    label: m.label,
    dimensions: m.dimensions || [],
  })),
  defaultMeasure: meta?.defaultMeasure || DEFAULT_FILTER.measure,
  kinds: meta?.kinds || [],
  severity: meta?.severity || [],
  buildings: (meta?.buildings || []).map((b: any) => ({
    id: b.id,
    name: b.name,
    portfolioId: b.portfolioId ?? null,
  })),
  portfolios: (meta?.portfolios || []).map((p: any) => ({ id: p.id, name: p.name })),
  latencySubjects: (meta?.latency?.subjects || []).map((s: any) => s.key),
  defaultSubject: meta?.latency?.defaultSubject || DEFAULT_FILTER.subject,
  turnoverBuckets: meta?.turnover?.buckets || [],
  defaultTurnoverBucket:
    meta?.turnover?.defaultBucket || DEFAULT_FILTER.turnoverBucket,
  qualityDrills: meta?.quality?.drills || [],
  rhythmMeasures: meta?.rhythm?.measures || [],
});
/* eslint-enable @typescript-eslint/no-explicit-any */

/* ──────────────────────  which fields a book can filter  ──────────────── */

/**
 * ═══════ THE MAP THAT MAKES THE 2026-08-14 DEFECT UNREPEATABLE ═══════
 *
 * The reports drawer decided field visibility with `isVisitorForm = (logType ===
 * 'Visitor Log')`, and then rendered the Building Activities select and the
 * Priority select under `!isVisitorForm` — which is TRUE for Parcels and for
 * Residents. Neither key is a column on either of those books, so the value fell
 * through to the server's JS equality pass, matched nothing, and the report came
 * back EMPTY with a completely reasonable-looking sidebar
 * (CONSOLIDATION_FINDINGS.md §8.2).
 *
 * "Which fields can this book filter on" is therefore a single declared map,
 * derived from SQL_FILTER_COLUMNS in lobbero_be/src/CRUD/reports.js plus the two
 * shared narrowings every book takes. The rail renders from it and the reducer
 * strips from it, so a field the selected book cannot answer is neither offered
 * nor sent.
 *
 * `unit` is here for all four: the other three books call it apartmentLocation
 * and the parcel arm translates it to unit_label server-side.
 */
export const BOOK_FIELDS: Record<Book, (keyof DashboardFilter)[]> = {
  'Visitor Log': [
    'visitorType',
    'visitorDetails',
    'preAuthorized',
    'onPass',
    'unit',
    'numberOfGuests',
    'approval',
    'parkingValidation',
    'name',
    'lastName',
  ],
  'Resident Log': [
    'unit',
    'numberOfGuests',
    'approval',
    'parkingValidation',
    'name',
    'lastName',
  ],
  // carrier and status are columns on `packages` and on no other table.
  // `name` is the recipient, which the parcel arm maps to a scalar.
  'Parcel Log': ['carrier', 'parcelStatus', 'unit', 'name'],
  'Daily Log': [
    'buildingActivity',
    'priority',
    'visitorDetails',
    'preAuthorized',
    'unit',
    'numberOfGuests',
    'approval',
    'parkingValidation',
    'name',
    'lastName',
  ],
};

/** Every field that is book-specific, i.e. cleared when the book changes. */
export const ALL_BOOK_FIELDS: (keyof DashboardFilter)[] = Array.from(
  new Set(Object.values(BOOK_FIELDS).flat())
  // `unit` is a SCOPE narrowing, not a book field — it survives a book change
  // for the same reason the building and the date range do: somebody switching
  // from Visitors to Parcels is asking the same question of a different table.
).filter(field => field !== 'unit');

export const bookCanFilter = (
  book: Book,
  field: keyof DashboardFilter
): boolean => BOOK_FIELDS[book].includes(field);
